<script setup lang="ts">
import {ref, computed, watch} from 'vue'
import type {Ref} from 'vue'
import {notifications as message} from '@/lib/notifications'
import {api} from '@/api'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'

const props = defineProps<{
  targetLang: string
  nativeLang: string
}>()

// Emit events for parent component
const emit = defineEmits(['settingsSaved'])

// Custom instructions state variables
const customDefaultInstructionsKey = computed(
  () => `translate_default_instructions_${props.targetLang}_${props.nativeLang}`
)
const customCommentedInstructionsKey = computed(
  () => `translate_commented_instructions_${props.targetLang}_${props.nativeLang}`
)
const customDefaultInstructions = ref<string>('')
const customCommentedInstructions = ref<string>('')
const originalDefaultInstructions = ref<string>('')
const originalCommentedInstructions = ref<string>('')
const isDefaultInstructionsChanged = computed(
  () => customDefaultInstructions.value !== originalDefaultInstructions.value
)
const isCommentedInstructionsChanged = computed(
  () => customCommentedInstructions.value !== originalCommentedInstructions.value
)

// Examples count state variables
const examplesCountKey = computed(() => `translate_examples_count_${props.targetLang}_${props.nativeLang}`)
const examplesCount = ref<number>(0)
const originalExamplesCount = ref<number>(0)
const isExamplesCountChanged = computed(() => examplesCount.value !== originalExamplesCount.value)

// Fine-tuning data download state variables
const fineTuningExamplesCount = ref<number>(50)
const isGeneratingFineTuningData = ref<boolean>(false)
const isGeneratingDefaultFineTuningData = ref<boolean>(false)
const isGeneratingCommentedFineTuningData = ref<boolean>(false)

// Any setting changed
const isAnySettingChanged = computed(
  () =>
    isDefaultInstructionsChanged.value ||
    isCommentedInstructionsChanged.value ||
    isExamplesCountChanged.value
)

// Load custom instructions and settings
async function loadSettings() {
  if (props.targetLang && props.nativeLang) {
    try {
      const [
        defaultInstructionsResponse,
        commentedInstructionsResponse,
        examplesCountResponse,
      ] = await Promise.all([
        api.get(`/api/cp/settings/${customDefaultInstructionsKey.value}`).catch(() => ({data: {value: ''}})),
        api.get(`/api/cp/settings/${customCommentedInstructionsKey.value}`).catch(() => ({data: {value: ''}})),
        api.get(`/api/cp/settings/${examplesCountKey.value}`).catch(() => ({data: {value: '0'}})),
      ])

      customDefaultInstructions.value = defaultInstructionsResponse.data?.value || ''
      originalDefaultInstructions.value = defaultInstructionsResponse.data?.value || ''
      customCommentedInstructions.value = commentedInstructionsResponse.data?.value || ''
      originalCommentedInstructions.value = commentedInstructionsResponse.data?.value || ''

      examplesCount.value = parseInt(examplesCountResponse.data?.value || '0')
      originalExamplesCount.value = parseInt(examplesCountResponse.data?.value || '0')

    } catch (error) {
      console.error('Failed to load settings:', error)
      // Keep the default values without resetting them to empty/zero
      if (!customDefaultInstructions.value) {
        customDefaultInstructions.value = ''
        originalDefaultInstructions.value = ''
      }
      if (!customCommentedInstructions.value) {
        customCommentedInstructions.value = ''
        originalCommentedInstructions.value = ''
      }
      if (!examplesCount.value) {
        examplesCount.value = 0
        originalExamplesCount.value = 0
      }
    }
  }
}

// Save custom instructions and settings
async function saveSettings() {
  if (props.targetLang && props.nativeLang) {
    try {
      const promises = []

      if (isDefaultInstructionsChanged.value) {
        promises.push(
          api.put(`/api/cp/settings/${customDefaultInstructionsKey.value}`, {
            value: customDefaultInstructions.value,
          })
        )
      }

      if (isCommentedInstructionsChanged.value) {
        promises.push(
          api.put(`/api/cp/settings/${customCommentedInstructionsKey.value}`, {
            value: customCommentedInstructions.value,
          })
        )
      }

      if (isExamplesCountChanged.value) {
        promises.push(
          api.put(`/api/cp/settings/${examplesCountKey.value}`, {
            value: examplesCount.value.toString(),
          })
        )
      }


      if (promises.length > 0) {
        await Promise.all(promises)

        // Update original values only for changed settings
        if (isDefaultInstructionsChanged.value) {
          originalDefaultInstructions.value = customDefaultInstructions.value
        }
        if (isCommentedInstructionsChanged.value) {
          originalCommentedInstructions.value = customCommentedInstructions.value
        }
        if (isExamplesCountChanged.value) {
          originalExamplesCount.value = examplesCount.value
        }

        message.success('Settings saved')
        emit('settingsSaved')
      }
    } catch (error) {
      console.error('Failed to save settings:', error)
      message.error('Failed to save settings')
    }
  }
}

// Helper method to download file from blob
function downloadBlobAsFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()

  // Clean up
  window.URL.revokeObjectURL(url)
  document.body.removeChild(link)
}

// Generic method to download fine-tuning data
async function downloadFineTuningDataGeneric(
  endpoint: string,
  loadingRef: Ref<boolean>,
  filenamePrefix: string,
  successMessage: string
) {
  if (!props.targetLang || !props.nativeLang) {
    message.error('Please select target and native languages')
    return
  }

  try {
    loadingRef.value = true

    // Create a form data object for the POST request
    const formData = {
      targetLanguage: props.targetLang,
      nativeLanguage: props.nativeLang,
      maxExamples: fineTuningExamplesCount.value,
    }

    // Make the API request
    const response = await api.post(endpoint, formData, {
      responseType: 'blob', // Important for handling file downloads
    })

    // Create a blob URL and trigger download
    const blob = new Blob([response.data], {type: 'application/jsonl'})
    const filename = `${filenamePrefix}_${props.targetLang}_${props.nativeLang}.jsonl`
    downloadBlobAsFile(blob, filename)

    message.success(`${successMessage} with ${fineTuningExamplesCount.value} examples generated successfully`)
  } catch (error) {
    console.error(`Failed to generate ${successMessage.toLowerCase()}:`, error)
    message.error(`Failed to generate ${successMessage.toLowerCase()}`)
  } finally {
    loadingRef.value = false
  }
}

// Method to download combined fine-tuning data
async function downloadFineTuningData() {
  await downloadFineTuningDataGeneric(
    '/api/cp/finetune/generate',
    isGeneratingFineTuningData,
    'finetune',
    'Fine-tuning data'
  )
}

// Method to download default fine-tuning data
async function downloadDefaultFineTuningData() {
  await downloadFineTuningDataGeneric(
    '/api/cp/finetune/generate-default',
    isGeneratingDefaultFineTuningData,
    'finetune_default',
    'Default fine-tuning data'
  )
}

// Method to download commented fine-tuning data
async function downloadCommentedFineTuningData() {
  await downloadFineTuningDataGeneric(
    '/api/cp/finetune/generate-commented',
    isGeneratingCommentedFineTuningData,
    'finetune_commented',
    'Commented fine-tuning data'
  )
}

// Watch for language changes to reload settings
watch(
  [() => props.targetLang, () => props.nativeLang],
  async () => {
    await loadSettings()
  },
  {immediate: true}
)

// Expose methods and properties to parent component
defineExpose({
  loadSettings,
  examplesCount,
})
</script>

<template>
  <div class="grid gap-6">
    <p class="text-sm text-muted-foreground">Настройки моделей ролей находятся в <a class="underline" href="/cp/settings">настройках моделей</a>.</p>
    <div class="grid gap-2">
      <Label for="default-instructions">Default Translation Instructions</Label>
      <Textarea
        id="default-instructions"
        v-model="customDefaultInstructions"
        :placeholder="$t('common.enterText')"
        class="min-h-32" />
    </div>
    <div class="grid gap-2">
      <Label for="commented-instructions">Commented Translation Instructions</Label>
      <Textarea
        id="commented-instructions"
        v-model="customCommentedInstructions"
        :placeholder="$t('common.enterText')"
        class="min-h-48" />
    </div>
      <div class="grid gap-2 max-w-xs">
       <div class="grid gap-2">
         <Label for="examples-count">Количество примеров</Label
         ><Input id="examples-count" v-model="examplesCount" type="number" min="0" max="20" />
       </div>
      </div>
    <div class="grid gap-4 border-t pt-6">
      <div>
        <h4 class="font-medium">Скачать данные для fine-tuning</h4>
        <p class="text-sm text-muted-foreground">Создайте набор данных с выбранным количеством примеров.</p>
      </div>
      <div class="grid max-w-xs gap-2">
        <Label for="fine-tuning-count">Количество примеров</Label
        ><Input id="fine-tuning-count" v-model="fineTuningExamplesCount" type="number" min="1" max="500" />
      </div>
      <div class="flex flex-wrap gap-2">
        <Button variant="outline" :disabled="isGeneratingFineTuningData" @click="downloadFineTuningData"
          >Скачать полные данные</Button
        ><Button variant="outline" :disabled="isGeneratingDefaultFineTuningData" @click="downloadDefaultFineTuningData"
          >Скачать данные для default</Button
        ><Button
          variant="outline"
          :disabled="isGeneratingCommentedFineTuningData"
          @click="downloadCommentedFineTuningData"
          >Скачать данные для commented</Button
        >
      </div>
    </div>
    <div class="flex justify-end border-t pt-6">
      <Button :disabled="!isAnySettingChanged" @click="saveSettings">Save Settings</Button>
    </div>
  </div>
</template>
